import React from 'react';
import { useNavigate } from 'react-router-dom';
import { nacmeLogo } from './assets/images';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 animate-fade-in">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 bg-white/40 backdrop-blur-md border-b border-gray-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-6">
          <img
            src={nacmeLogo}
            alt="NACME Logo"
            className="h-10 w-auto drop-shadow-sm"
          />
          <div className="relative group">
            <button className="text-sm font-semibold text-gray-700 hover:text-blue-600 transition-colors px-2 sm:px-4 py-2 rounded-lg hover:bg-blue-50/50">
              About
            </button>
            {/* Tooltip on hover */}
            <div className="absolute left-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-[40rem] md:w-[50rem] lg:w-[70rem] max-w-4xl p-4 sm:p-5 bg-white/95 backdrop-blur-md rounded-xl shadow-xl border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
              <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                NACME offers our recruiting partners access to high-achieving scholars and alumni for roles in engineering and computer science. Uniquely situated at the nexus of academia and industry, NACME is your pipeline to entry level, mid-career, and executive talent. Dedicated to fostering the next generation of engineers and computer scientists, we identify high-potential students at leading institutions and cultivate their academic and professional success with scholarships, programs, and career access.
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/student/login')}
            className="text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent hover:from-blue-700 hover:to-indigo-700 transition-all px-2 sm:px-4 py-2 rounded-lg hover:bg-blue-50/50"
          >
            Student Login
          </button>
          <button
            onClick={() => navigate('/recruiter/login')}
            className="text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent hover:from-purple-700 hover:to-pink-700 transition-all px-2 sm:px-4 py-2 rounded-lg hover:bg-purple-50/50"
          >
            Recruiter Login
          </button>
          <button
            onClick={() => navigate('/partner/login')}
            className="text-sm font-semibold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent hover:from-teal-700 hover:to-cyan-700 transition-all px-2 sm:px-4 py-2 rounded-lg hover:bg-teal-50/50"
          >
            .edu Partner Login
          </button>
          <button
            onClick={() => navigate('/alumni/login')}
            className="text-sm font-semibold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent hover:from-orange-700 hover:to-amber-700 transition-all px-2 sm:px-4 py-2 rounded-lg hover:bg-orange-50/50"
          >
            Alumni Login
          </button>
          <button
            onClick={() => navigate('/blog')}
            className="text-sm font-semibold text-gray-700 hover:text-blue-600 transition-colors px-2 sm:px-4 py-2 rounded-lg hover:bg-blue-50/50"
          >
            Blog
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
        {/* Hero Section */}
        <div className="text-center mb-12 sm:mb-16 md:mb-20 mt-2">
          <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mb-3 leading-tight pb-2">
            Welcome to NACME Searchlight
          </h1>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto font-light">
            Connecting talent with leading companies for employment and internship opportunities
          </p>
        </div>

        {/* Main Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10 sm:mb-12 md:mb-16">
          {/* Students Section */}
          <div className="group relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 text-center border border-blue-200 hover:border-blue-400 hover:-translate-y-2 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-indigo-600/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent mb-3">For Students</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Login to check your dashboard and connect with top employers
              </p>
              <button
                onClick={() => navigate('/student/login')}
                className="w-full px-6 py-3.5 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:via-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Student Login
              </button>
            </div>
          </div>

          {/* Companies Section */}
          <div className="group relative bg-gradient-to-br from-emerald-50 via-white to-teal-50 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 text-center border border-emerald-200 hover:border-emerald-400 hover:-translate-y-2 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-teal-600/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-emerald-400 via-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-emerald-700 to-teal-700 bg-clip-text text-transparent mb-3">Browse Companies</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Explore all partner companies and their job/internship opportunities
              </p>
              <button
                onClick={() => navigate('/companies')}
                className="w-full px-6 py-3.5 bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600 text-white font-semibold rounded-xl hover:from-emerald-600 hover:via-emerald-700 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Explore Now
              </button>
            </div>
          </div>

          {/* Recruiters Section */}
          <div className="group relative bg-gradient-to-br from-purple-50 via-white to-pink-50 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 text-center border border-purple-200 hover:border-purple-400 hover:-translate-y-2 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-600/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-purple-400 via-purple-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-700 bg-clip-text text-transparent mb-3">For Recruiters</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Login and find talented hires who match your job and internship requirements
              </p>
              <button
                onClick={() => navigate('/recruiter/login')}
                className="w-full px-6 py-3.5 bg-gradient-to-r from-purple-500 via-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:from-purple-600 hover:via-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Recruiter Login
              </button>
            </div>
          </div>

          {/* Alumni Section */}
          <div className="group relative bg-gradient-to-br from-orange-50 via-white to-amber-50 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 text-center border border-orange-200 hover:border-orange-400 hover:-translate-y-2 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-amber-600/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-orange-400 via-orange-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-orange-700 to-amber-700 bg-clip-text text-transparent mb-3">Alumni Portal</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Access exclusive job opportunities and expand your NACME network
              </p>
              <button
                onClick={() => navigate('/alumni/login')}
                className="w-full px-6 py-3.5 bg-gradient-to-r from-orange-500 via-orange-600 to-amber-600 text-white font-semibold rounded-xl hover:from-orange-600 hover:via-orange-700 hover:to-amber-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Access Portal
              </button>
            </div>
          </div>
        </div>

        {/* Company Logos Carousel */}
        <div className="mb-10 sm:mb-12 md:mb-16">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center text-gray-800 mb-6 sm:mb-8">
            Our Partner Companies
          </h2>
          <div className="relative overflow-hidden py-6 sm:py-8">
            <div className="flex animate-scroll">
              {/* Set A */}
              <div className="flex items-center shrink-0 gap-8 sm:gap-12 md:gap-16" key="logo-set-a">
                <img src="/company_logos/scroll/air_products.png" alt="Air Products" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 hover:opacity-100 transition-opacity duration-300" />
                <img src="/company_logos/scroll/autodesk.png" alt="Autodesk" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 hover:opacity-100 transition-opacity duration-300" />
                <img src="/company_logos/scroll/bechtel.png" alt="Bechtel" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 hover:opacity-100 transition-opacity duration-300" />
                <img src="/company_logos/scroll/bsci.png" alt="BSCI" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 hover:opacity-100 transition-opacity duration-300" />
                <img src="/company_logos/scroll/deloitte.png" alt="Deloitte" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 hover:opacity-100 transition-opacity duration-300" />
                <img src="/company_logos/scroll/ge_healthcare.png" alt="GE Healthcare" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 hover:opacity-100 transition-opacity duration-300" />
                <img src="/company_logos/scroll/ge_vernova.png" alt="GE Vernova" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 hover:opacity-100 transition-opacity duration-300" />
                <img src="/company_logos/scroll/global_foundries.png" alt="GlobalFoundries" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 hover:opacity-100 transition-opacity duration-300" />
                <img src="/company_logos/scroll/ibm.png" alt="IBM" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 hover:opacity-100 transition-opacity duration-300" />
                <img src="/company_logos/scroll/itt.png" alt="ITT" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 hover:opacity-100 transition-opacity duration-300" />
                <img src="/company_logos/scroll/merck.png" alt="Merck" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 hover:opacity-100 transition-opacity duration-300" />
                <img src="/company_logos/scroll/pg.png" alt="P&G" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 hover:opacity-100 transition-opacity duration-300" />
                <img src="/company_logos/scroll/p66.png" alt="Phillips 66" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 hover:opacity-100 transition-opacity duration-300" />
                <img src="/company_logos/scroll/ups.png" alt="UPS" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 hover:opacity-100 transition-opacity duration-300" />
              </div>

              {/* Set B (exact duplicate) */}
              <div className="flex items-center shrink-0 gap-8 sm:gap-12 md:gap-16 mr-32 sm:mr-48 md:mr-64" aria-hidden="true" key="logo-set-b">
                <img src="/company_logos/scroll/air_products.png" alt="" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 transition-opacity duration-300" />
                <img src="/company_logos/scroll/autodesk.png" alt="" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 transition-opacity duration-300" />
                <img src="/company_logos/scroll/bechtel.png" alt="" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 transition-opacity duration-300" />
                <img src="/company_logos/scroll/bsci.png" alt="" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 transition-opacity duration-300" />
                <img src="/company_logos/scroll/deloitte.png" alt="" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 transition-opacity duration-300" />
                <img src="/company_logos/scroll/ge_healthcare.png" alt="" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 transition-opacity duration-300" />
                <img src="/company_logos/scroll/ge_vernova.png" alt="" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 transition-opacity duration-300" />
                <img src="/company_logos/scroll/global_foundries.png" alt="" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 transition-opacity duration-300" />
                <img src="/company_logos/scroll/ibm.png" alt="" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 transition-opacity duration-300" />
                <img src="/company_logos/scroll/itt.png" alt="" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 transition-opacity duration-300" />
                <img src="/company_logos/scroll/merck.png" alt="" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 transition-opacity duration-300" />
                <img src="/company_logos/scroll/pg.png" alt="" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 transition-opacity duration-300" />
                <img src="/company_logos/scroll/p66.png" alt="" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 transition-opacity duration-300" />
                <img src="/company_logos/scroll/ups.png" alt="" className="h-10 sm:h-12 md:h-16 w-auto flex-none opacity-80 transition-opacity duration-300" />
              </div>
            </div>
          </div>
        </div>

        {/* Additional Links */}
        <div className="text-center">
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6 md:p-8 max-w-4xl mx-auto mb-4 sm:mb-6 md:mb-8">
            <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
              <button
                onClick={() => navigate('/register')}
                className="px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg text-sm sm:text-base"
              >
                Student App
              </button>
              <button
                onClick={() => navigate('/recruiter-signup')}
                className="px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-700 transition-all duration-300 shadow-md hover:shadow-lg text-sm sm:text-base"
              >
                Recruiter Signup
              </button>
              <button
                onClick={() => navigate('/partner/login')}
                className="px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-semibold rounded-xl hover:from-teal-600 hover:to-cyan-700 transition-all duration-300 shadow-md hover:shadow-lg text-sm sm:text-base"
              >
                .edu Partner Login
              </button>
              <button
                onClick={() => navigate('/alumni/register')}
                className="px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-amber-700 transition-all duration-300 shadow-md hover:shadow-lg text-sm sm:text-base"
              >
                Alumni Signup
              </button>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
              <img
                src="/ssl.png"
                alt="SSL Secured"
                className="h-10 sm:h-12 w-auto opacity-80"
              />
              <div className="text-center sm:text-left">
                Tech support? Contact{' '}
                <a
                  href="mailto:help@nacme.app"
                  className="text-[#00A0DF] hover:text-[#0090CF] font-semibold transition-colors"
                >
                  help@nacme.app
                </a>
                {' '} | {' '}
                <span className="text-gray-500">1432 Duke Street, Alexandria, Virginia 22314</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 mt-3">
              <span className="text-xs text-gray-500">Powered by</span>
              <img
                src="/moonsphinx2.png"
                alt="MoonSphinx"
                className="h-2.5 w-auto opacity-80"
              />
            </div>
          </div>

          <div className="pt-2 sm:pt-4 pb-4 sm:pb-6">
            <button
              onClick={() => navigate('/admin')}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              Admin Portal
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
